import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import Cookies from 'js-cookie';
import adminApiAxiosInstance from './adminApi';
import { CITY_COOKIE_KEY, CITY_HEADER_KEY, DEVICE_HEADER_KEY, LOCALE_COOKIE_KEY, LOCALE_HEADER_KEY } from '@/shared/constants/global';
import { removeToken, setToken } from '@/utils/auth';
import { refreshTokenFx, refreshTokenStarted } from '@/shared/store/auth';

export const publicApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || '/api',
  withCredentials: true,
});

type PublicApiRequestConfig = AxiosRequestConfig & {
  skipAuthRedirect?: boolean;
  _retry?: boolean;
};

publicApi.interceptors.request.use(async (config) => {
  if (typeof document === 'undefined') return config;

  if (config.url === '/auth/refresh-tokens') return config;

  config.headers ??= {} as AxiosRequestHeaders;

  /* ------------------ locale / city ------------------ */

  const locale = getSafeLocale();
  const citySlug = getSafeCity();
  const device = document.documentElement.dataset.device || 'desktop';

  if (locale) {
    config.headers[LOCALE_HEADER_KEY] = locale;
  }

  if (citySlug) {
    config.headers[CITY_HEADER_KEY] = citySlug;
  }

  if (device) {
    config.headers[DEVICE_HEADER_KEY] = device;
  }

  /* ------------------ auth ------------------ */

  const token = Cookies.get('access_token');
  if (token) {
    // Есть токен → проверяем, не просрочен ли (через 401 потом)
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Токена нет → гость, не ждём refresh, просто идём дальше
    return config;
  }

  // Если токен есть, но уже идёт refresh, ждём его
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({
        resolve: (newToken: string) => {
          config.headers.Authorization = `Bearer ${newToken}`;
          resolve(config);
        },
        reject,
      });
    });
  }

  return config;
});


let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

publicApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as PublicApiRequestConfig;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== '/auth/refresh-tokens'
    ) {
      originalRequest._retry = true;

      refreshTokenStarted();
      // ⛔️ Если уже идёт refresh — встаём в очередь
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (token) {
            originalRequest.headers ??= {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return publicApi(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const newToken = await refreshTokenFx();
        processQueue(null, newToken);

        originalRequest.headers ??= {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        return publicApi(originalRequest);
      } catch (e) {
        processQueue(e, null);
        // ❗️ учёт skipAuthRedirect
        if (!originalRequest.skipAuthRedirect) {
          if ((window as any).__logout) (window as any).__logout();
        } else {
          if ((window as any).__logoutWithoutRedirect) (window as any).__logoutWithoutRedirect();
        }
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function getSafeLocale() {
  return (
    Cookies.get(LOCALE_COOKIE_KEY) ||
    (typeof navigator !== 'undefined'
      ? navigator.language?.split('-')[0]
      : null) ||
    'ru'
  );
}

function getSafeCity() {
  return Cookies.get(CITY_COOKIE_KEY) || null;
}

export const adminApi = adminApiAxiosInstance;